import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from 'config/axiosBaseQuery';
import { endpoints } from 'config/api';
import {
  AdminCompany,
  AdminUser,
  CompanyQueryParams,
  UserQueryParams,
  CompanyStats,
  UserStats,
  BulkCompanyOperation,
  BulkUserOperation,
  UpdateSubscription,
  PaginatedResponse,
  SubscriptionStatusResponse,
  UpdateSeatsRequest,
  UpdateSeatsResponse,
  CancelSubscriptionRequest,
  CancelSubscriptionResponse,
  ReactivateSubscriptionResponse,
  SeedDemoDataRequest,
  DemoSeedResult,
  DemoRemoveResult,
} from './adminTypes';

export const adminApi = createApi({
  reducerPath: 'adminApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['AdminCompanies', 'AdminUsers', 'AdminStats'],
  endpoints: (builder) => ({
    // Companies
    getCompanies: builder.query<PaginatedResponse<AdminCompany>, CompanyQueryParams | void>({
      query: (params) => ({
        url: endpoints.companies.base,
        method: 'GET',
        params: params || {},
      }),
      providesTags: ['AdminCompanies'],
    }),

    getCompanyStats: builder.query<CompanyStats, void>({
      query: () => ({
        url: endpoints.companies.stats,
        method: 'GET',
      }),
      providesTags: ['AdminStats'],
    }),

    updateCompany: builder.mutation<AdminCompany, { id: string; data: Partial<AdminCompany> }>({
      query: ({ id, data }) => ({
        url: endpoints.companies.byId(id),
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['AdminCompanies', 'AdminStats'],
    }),

    updateCompanySubscription: builder.mutation<{ success: boolean; company: AdminCompany }, { id: string; data: UpdateSubscription }>({
      query: ({ id, data }) => ({
        url: endpoints.companies.subscription(id),
        method: 'PATCH',
        data,
      }),
      invalidatesTags: ['AdminCompanies', 'AdminStats'],
    }),

    deleteCompany: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: endpoints.companies.byId(id),
        method: 'DELETE',
      }),
      invalidatesTags: ['AdminCompanies', 'AdminStats'],
    }),

    bulkCompanyOperation: builder.mutation<{ success: boolean; message: string; modifiedCount: number }, BulkCompanyOperation>({
      query: (data) => ({
        url: endpoints.companies.bulk,
        method: 'PATCH',
        data,
      }),
      invalidatesTags: ['AdminCompanies', 'AdminStats'],
    }),

    transferCompanyOwner: builder.mutation<{ success: boolean; message: string; company: AdminCompany }, { companyId: string; newOwnerId: string }>({
      query: ({ companyId, newOwnerId }) => ({
        url: endpoints.companies.transferOwner(companyId),
        method: 'PATCH',
        data: { newOwnerId },
      }),
      invalidatesTags: ['AdminCompanies', 'AdminUsers'],
    }),

    // Users
    getAllUsers: builder.query<PaginatedResponse<AdminUser>, UserQueryParams | void>({
      query: (params) => ({
        url: endpoints.users.all,
        method: 'GET',
        params: params || {},
      }),
      providesTags: ['AdminUsers'],
    }),

    getUserStats: builder.query<UserStats, void>({
      query: () => ({
        url: endpoints.users.stats,
        method: 'GET',
      }),
      providesTags: ['AdminStats'],
    }),

    createUser: builder.mutation<AdminUser, Partial<AdminUser> & { password: string; company: string }>({
      query: (data) => ({
        url: endpoints.users.base,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['AdminUsers', 'AdminStats', 'AdminCompanies'],
    }),

    updateUser: builder.mutation<AdminUser, { id: string; data: Partial<AdminUser> }>({
      query: ({ id, data }) => ({
        url: endpoints.users.asAdmin(id),
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['AdminUsers', 'AdminStats'],
    }),

    deleteUser: builder.mutation<void, string>({
      query: (id) => ({
        url: endpoints.users.byId(id),
        method: 'DELETE',
      }),
      invalidatesTags: ['AdminUsers', 'AdminStats', 'AdminCompanies'],
    }),

    activateUser: builder.mutation<AdminUser, string>({
      query: (id) => ({
        url: endpoints.users.activate(id),
        method: 'PUT',
      }),
      invalidatesTags: ['AdminUsers', 'AdminStats', 'AdminCompanies'],
    }),

    deactivateUser: builder.mutation<AdminUser, string>({
      query: (id) => ({
        url: endpoints.users.deactivate(id),
        method: 'PUT',
      }),
      invalidatesTags: ['AdminUsers', 'AdminStats', 'AdminCompanies'],
    }),

    bulkUserOperation: builder.mutation<{ success: boolean; message: string; modifiedCount?: number; deletedCount?: number }, BulkUserOperation>({
      query: (data) => ({
        url: endpoints.users.bulk,
        method: 'PATCH',
        data,
      }),
      invalidatesTags: ['AdminUsers', 'AdminStats', 'AdminCompanies'],
    }),

    // Subscriptions
    getSubscriptionStatus: builder.query<SubscriptionStatusResponse, string>({
      query: (companyId) => ({
        url: endpoints.subscriptions.status(companyId),
        method: 'GET',
      }),
      providesTags: ['AdminCompanies'],
    }),

    updateSubscriptionSeats: builder.mutation<UpdateSeatsResponse, { companyId: string; data: UpdateSeatsRequest }>({
      query: ({ companyId, data }) => ({
        url: endpoints.subscriptions.updateSeats(companyId),
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['AdminCompanies', 'AdminStats'],
    }),

    cancelSubscription: builder.mutation<CancelSubscriptionResponse, { companyId: string; data?: CancelSubscriptionRequest }>({
      query: ({ companyId, data }) => ({
        url: endpoints.subscriptions.cancel(companyId),
        method: 'POST',
        data: data || { cancelAtPeriodEnd: true },
      }),
      invalidatesTags: ['AdminCompanies', 'AdminStats'],
    }),

    reactivateSubscription: builder.mutation<ReactivateSubscriptionResponse, string>({
      query: (companyId) => ({
        url: endpoints.subscriptions.reactivate(companyId),
        method: 'POST',
      }),
      invalidatesTags: ['AdminCompanies', 'AdminStats'],
    }),

    // Demo Seeding
    seedDemoData: builder.mutation<DemoSeedResult, SeedDemoDataRequest>({
      query: (data) => ({
        url: endpoints.demoSeeding.seed,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['AdminCompanies', 'AdminStats'],
    }),

    removeDemoData: builder.mutation<DemoRemoveResult, string>({
      query: (companyId) => ({
        url: endpoints.demoSeeding.remove(companyId),
        method: 'DELETE',
      }),
      invalidatesTags: ['AdminCompanies', 'AdminStats'],
    }),
  }),
});

export const {
  // Companies
  useGetCompaniesQuery,
  useGetCompanyStatsQuery,
  useUpdateCompanyMutation,
  useUpdateCompanySubscriptionMutation,
  useDeleteCompanyMutation,
  useBulkCompanyOperationMutation,
  useTransferCompanyOwnerMutation,
  // Users
  useGetAllUsersQuery,
  useGetUserStatsQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useActivateUserMutation,
  useDeactivateUserMutation,
  useBulkUserOperationMutation,
  // Subscriptions
  useGetSubscriptionStatusQuery,
  useUpdateSubscriptionSeatsMutation,
  useCancelSubscriptionMutation,
  useReactivateSubscriptionMutation,
  // Demo Seeding
  useSeedDemoDataMutation,
  useRemoveDemoDataMutation,
} = adminApi;
